import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough } from "lucide-react";

// Stable references so useEffect deps don't churn on every render.
const EMPTY_STR = "";

/**
 * Lightweight rich-text editor (iter-213).
 *
 * Restricted to the shared backend's HTML sanitizer whitelist:
 *   tags:   b, i, u, s, p, br, ul, ol, li, span
 *   styles: color, background-color, font-size  (inline `style=` only)
 *
 * We deliberately use `document.execCommand` — deprecated but universally
 * supported by evergreen browsers today. Two important toggles:
 *
 *   • For B / I / U / S we call `styleWithCSS(false)` so Chromium emits the
 *     legacy semantic tags <b>, <i>, <u>, <strike> — all whitelisted.
 *     (With CSS mode on, Chromium would emit `<span style="font-weight:bold">`
 *     and the sanitizer would strip it, losing the format.)
 *   • For colour / highlight / font-size we call `styleWithCSS(true)` so the
 *     command emits `<span style="…">` which IS whitelisted.
 */
export default function RichTextEditor({
  value,
  onChange,
  onPlainChange,
  testId = "rich-text-editor",
  placeholder = "Write your description…",
  maxLength = 60000,
}) {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(EMPTY_STR);
  const [focused, setFocused] = useState(false);

  // Sync incoming `value` into the DOM without stomping the user's caret.
  useEffect(() => {
    if (!editorRef.current) return;
    if ((value || EMPTY_STR) !== lastEmittedRef.current) {
      editorRef.current.innerHTML = value || EMPTY_STR;
      lastEmittedRef.current = value || EMPTY_STR;
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    // Soft-cap length: block additional input beyond maxLength rather than
    // slicing raw HTML (which could cut mid-tag). We simply refuse to accept
    // anything past the boundary by resetting to the last-emitted value.
    if (editorRef.current.innerHTML.length > maxLength) {
      editorRef.current.innerHTML = lastEmittedRef.current;
    }
    const html = editorRef.current.innerHTML;
    lastEmittedRef.current = html;
    onChange?.(html);
    if (onPlainChange) onPlainChange(editorRef.current.innerText || EMPTY_STR);
  }, [onChange, onPlainChange, maxLength]);

  // Semantic commands (B/I/U/S) — MUST use styleWithCSS=false so Chromium
  // emits <b>/<i>/<u>/<strike>, all of which pass the backend sanitizer.
  const execSemantic = (command) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand(command, false, null);
    emit();
  };

  // Style commands (colour / highlight / size) — MUST use styleWithCSS=true
  // so the emitted markup is `<span style="…">` matching the whitelist.
  const execStyled = (command, arg) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand(command, false, arg);
    emit();
  };

  const setColor = (c) => execStyled("foreColor", c);
  const setHighlight = (c) => execStyled("hiliteColor", c);
  const setSize = (px) => {
    // execCommand("fontSize") only accepts 1-7; wrap the selection with a
    // placeholder size 7 span first, then rewrite its inline style to the
    // requested pixel value so the sanitizer accepts it.
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("fontSize", false, "7");
    editorRef.current.querySelectorAll('span[style*="font-size"]').forEach((s) => {
      if (s.style.fontSize === "xxx-large" || s.style.fontSize === "48px") {
        s.style.fontSize = `${px}px`;
      }
    });
    emit();
  };

  const isEmpty = !value || value === EMPTY_STR || value === "<br>" || value === "<p><br></p>";

  return (
    <div className="rounded-xl border border-soft bg-white overflow-hidden" data-testid={`${testId}-wrap`}>
      <div className="flex items-center flex-wrap gap-1 px-2 py-1.5 border-b border-subtle bg-slate-50">
        <TbButton label="Bold" onClick={() => execSemantic("bold")} testId={`${testId}-btn-bold`}><Bold className="w-3.5 h-3.5" /></TbButton>
        <TbButton label="Italic" onClick={() => execSemantic("italic")} testId={`${testId}-btn-italic`}><Italic className="w-3.5 h-3.5" /></TbButton>
        <TbButton label="Underline" onClick={() => execSemantic("underline")} testId={`${testId}-btn-underline`}><UnderlineIcon className="w-3.5 h-3.5" /></TbButton>
        <TbButton label="Strikethrough" onClick={() => execSemantic("strikeThrough")} testId={`${testId}-btn-strike`}><Strikethrough className="w-3.5 h-3.5" /></TbButton>

        <span className="mx-1 w-px h-5 bg-slate-200" />

        {/* 4 sizes: S · M · L · XL → 12 / 15 / 19 / 24 px per spec */}
        {[
          { label: "S", px: 12 },
          { label: "M", px: 15 },
          { label: "L", px: 19 },
          { label: "XL", px: 24 },
        ].map((s) => (
          <TbButton
            key={s.label}
            label={`Size ${s.label}`}
            onClick={() => setSize(s.px)}
            testId={`${testId}-btn-size-${s.label.toLowerCase()}`}
          >
            <span className="text-[11px] font-bold" style={{ fontSize: `${Math.min(s.px, 14)}px` }}>{s.label}</span>
          </TbButton>
        ))}

        <span className="mx-1 w-px h-5 bg-slate-200" />

        {/* 6 text colour swatches */}
        <SwatchStrip
          testId={`${testId}-color`}
          swatches={["#0f172a", "#dc2626", "#ea580c", "#16a34a", "#2563eb", "#7c3aed"]}
          onPick={setColor}
          label="Text colour"
        />

        {/* 4 highlight swatches */}
        <SwatchStrip
          testId={`${testId}-highlight`}
          swatches={["#fef3c7", "#dcfce7", "#dbeafe", "#fce7f3"]}
          onPick={setHighlight}
          label="Highlight"
          hollowBorder
        />
      </div>

      <div className="relative">
        {isEmpty && !focused && (
          <div className="absolute top-3 left-3 text-slate-400 pointer-events-none text-sm">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          data-testid={testId}
          contentEditable
          onInput={emit}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          suppressContentEditableWarning
          className="min-h-[10rem] px-3 py-2.5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-[color:var(--brand-blue)]/30 rich-editor-body"
        />
      </div>
    </div>
  );
}

function TbButton({ children, label, onClick, testId }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      data-testid={testId}
      onMouseDown={(e) => {
        // Prevent focus loss from the editor so the current selection
        // stays intact when the toolbar button is clicked.
        e.preventDefault();
      }}
      onClick={onClick}
      className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-white text-slate-700 transition-colors"
    >
      {children}
    </button>
  );
}

function SwatchStrip({ swatches, onPick, label, testId, hollowBorder }) {
  return (
    <div className="inline-flex items-center gap-0.5" role="group" aria-label={label} data-testid={testId}>
      {swatches.map((c) => (
        <button
          key={c}
          type="button"
          title={`${label} · ${c}`}
          aria-label={`${label} ${c}`}
          data-testid={`${testId}-${c.replace("#", "").toLowerCase()}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(c)}
          className={`w-5 h-5 rounded-full ${hollowBorder ? "border border-slate-300" : ""}`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}
